import { useCreateCarePlanMutation } from '~/store/services/carePlan';
import {
  ActionBar,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  FAIconName,
  FireflyButton,
  ModalV2,
  RadioV2,
  Text,
  ToastMessage,
  useChakraToast,
} from '@equip.health/ui';
import { useState } from 'react';
import { CarePlanStatus } from '~/lib/types/carePlan/CarePlanStatus';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { isDraftNotesConflictError } from '~/lib/util/carePlan.util';
import { DraftNotesConflictModal } from './DraftNotesConflictModal';
import { SUPPORT_EMAIL } from '~/lib/constants/global.constants';

const TOP_LEVEL_ID = 'create-pathway-modal';
type Props = {
  treatmentEpisodeId: string;
  onClose: () => void;
};
export const CreateCarePlanModal = ({ treatmentEpisodeId, onClose }: Props) => {
  const { trackEvent } = useAnalytics();
  const [
    createPathway,
    { isLoading: isCreatePathwayLoading, error: createPathwayError },
  ] = useCreateCarePlanMutation();

  const [includeCurrentData, setIncludeCurrentData] = useState(true);
  const [isDraftConflictModalOpen, setIsDraftConflictModalOpen] =
    useState(false);

  const chakraToast = useChakraToast();

  const showError =
    !!createPathwayError && !isDraftNotesConflictError(createPathwayError);

  return (
    <ModalV2
      isOpen
      onClose={() => {
        trackEvent(CarePlanEvents.clickOnCloseIconInCreateNewPlanModal);
        onClose();
      }}
      size="md"
      title="Create new care plan"
    >
      <Text>
        Please note, only one care plan can be active at a time.
        <br />
        The current plan will be closed and the new one will take its place.
      </Text>
      <Text color="neutral.secondary" marginTop="24px" variant="caption">
        Would you like to use the current problems and goals?
      </Text>
      <ChakraFlex flexDirection="column" gap="18px" marginTop="15px">
        <RadioV2
          id={`${TOP_LEVEL_ID}_use_current_true`}
          isChecked={includeCurrentData}
          label="Include current problems and goals in new plan"
          onChange={() => {
            trackEvent(
              CarePlanEvents.clickOnIncludeCurrentProblemsAndGoalsInNewPlanInCreateNewPlanModal,
            );
            setIncludeCurrentData(true);
          }}
        />
        <RadioV2
          id={`${TOP_LEVEL_ID}_use_current_false`}
          isChecked={!includeCurrentData}
          label="Start from scratch with no problems or goals"
          onChange={() => {
            trackEvent(
              CarePlanEvents.clickOnStartFromScratchWithNoProblemsOrGoalsButtonInCreateNewPlanModal,
            );
            setIncludeCurrentData(false);
          }}
        />
      </ChakraFlex>

      {showError && (
        <ChakraBox marginTop="24px">
          <ActionBar
            barIcon={FAIconName.exclamationTriangleRegular}
            id={`${TOP_LEVEL_ID}-error-banner`}
            text={
              <ChakraText fontSize="15px" fontWeight={400} lineHeight="1.25">
                {`We're sorry, we were unable to create a new care plan. Please try again or reach out to `}
                <ChakraText
                  as="a"
                  href={`mailto:${SUPPORT_EMAIL}`}
                  textDecoration="underline"
                >
                  {SUPPORT_EMAIL}
                </ChakraText>
              </ChakraText>
            }
            variant="critical"
          />
        </ChakraBox>
      )}

      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginTop="24px"
        width="100%"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}-cancel-button`}
          isLoading={isCreatePathwayLoading}
          leftIcon={FAIconName.timesRegular}
          onClick={() => {
            trackEvent(CarePlanEvents.clickOnCancelButtonInCreateNewPlanModal);
            onClose();
          }}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}-create-button`}
          isLoading={isCreatePathwayLoading}
          leftIcon={FAIconName.checkRegular}
          onClick={async () => {
            trackEvent(
              CarePlanEvents.clickOnCreateNewCarePlanButtonInCreateNewPlanModal,
            );
            try {
              await createPathway({
                treatmentEpisodeId,
                includeCurrent: includeCurrentData,
                statusCode: CarePlanStatus.ACTIVE,
              }).unwrap();

              chakraToast({
                containerStyle: { marginBottom: '8px' },
                duration: 3000,
                id: 'create-care-plan-success_toast',
                position: 'bottom',
                render: ({ onClose: onToastClose }) => (
                  <ToastMessage
                    icon={FAIconName.checkRegular}
                    id="create-care-plan-success_toast"
                    onClose={onToastClose}
                    text="New care plan created. The previous care plan has been closed."
                    variant="success"
                  />
                ),
              });
              onClose();
            } catch (error) {
              if (isDraftNotesConflictError(error)) {
                setIsDraftConflictModalOpen(true);
              }
            }
          }}
          variant="primary"
        >
          Create new care plan
        </FireflyButton>
      </ChakraFlex>
      <DraftNotesConflictModal
        isOpen={isDraftConflictModalOpen}
        onClose={() => setIsDraftConflictModalOpen(false)}
        onCloseParent={onClose}
        operation="create"
      />
    </ModalV2>
  );
};
